"use client";

import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";

export const DURATION_PRESETS = [
  { minutes: 30, label: "30m" },
  { minutes: 45, label: "45m" },
  { minutes: 60, label: "1h" },
  { minutes: 75, label: "1h 15m" },
  { minutes: 90, label: "1h 30m" },
  { minutes: 120, label: "2h" },
];

export function formatDurationLabel(minutes: number): string {
  if (minutes <= 0) return "0m";
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (hours > 0 && remainingMins > 0) {
    return `${hours}h ${remainingMins}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${remainingMins}m`;
}

export function calculateDurationMinutes(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return 0;

  const startTotal = sh * 60 + sm;
  let endTotal = eh * 60 + em;
  if (endTotal < startTotal) {
    endTotal += 24 * 60; // Crosses midnight
  }
  return endTotal - startTotal;
}

export function addMinutesToTimeString(time: string, minutes: number): string {
  const [h, m] = time.split(":").map(Number);
  const validH = isNaN(h) ? 0 : h;
  const validM = isNaN(m) ? 0 : m;
  let total = (validH * 60 + validM + minutes) % (24 * 60);
  if (total < 0) total += 24 * 60;
  const newH = Math.floor(total / 60);
  const newM = total % 60;
  return `${newH.toString().padStart(2, "0")}:${newM.toString().padStart(2, "0")}`;
}

interface NapDurationPickerProps {
  startTime: string;
  endTime: string;
  onDurationSelect: (minutes: number) => void;
  className?: string;
}

export function NapDurationPicker({
  startTime,
  endTime,
  onDurationSelect,
  className,
}: NapDurationPickerProps) {
  const currentDuration = useMemo(() => {
    return calculateDurationMinutes(startTime, endTime);
  }, [startTime, endTime]);

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1 font-medium">
          <Clock className="h-3.5 w-3.5 text-primary" />
          <span>Nap Duration</span>
        </span>
        {currentDuration > 0 && (
          <span className="font-semibold text-primary">
            {formatDurationLabel(currentDuration)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-6 gap-1">
        {DURATION_PRESETS.map(({ minutes, label }) => {
          const isSelected = currentDuration === minutes;
          return (
            <Button
              key={minutes}
              type="button"
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => onDurationSelect(minutes)}
              className={cn(
                "h-8 px-1 text-xs font-medium touch-manipulation transition-all",
                isSelected
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "hover:bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              {label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
